import { anotarSalida, registrarEvento } from "../core/auditLog.js";
import { continuarLoginConMFA, renderPantallaCambioPassword, renderPantallaLogin } from "./login.js";
import { renderPantallaMFAObligatorio } from "./mfa.js";
import { cargarConfigsNube, cargarEntidadesNube, descargarSnapshotDeNube, getRolesPersonalizados, getRolesSistemaOverrides, loadData, obtenerTenantIdDelUsuario, suscribirRealtimeTenant } from "../core/cloudSync.js";
import { iniciarTrackerUso } from "../core/usoTracker.js";
import { cargarModulosHabilitados, moduloHabilitado, suscribirModulosRealtime } from "../core/modulos.js";
import { EMPRESA, ROLE_PAGES, ROLES_INFO } from "../core/config.js";
import { state } from "../core/state.js";
import { cloudState, supa } from "../core/supabaseClient.js";
import { attachListeners } from "../ui/actions.js";
import { render } from "../ui/app.js";
import { abrirSplash, getSplashConfig } from "../ui/splash.js";
import { iniciarAvisosDeSeguridad } from "../ui/avisoSeguridad.js";
import { iniciarVigilanciaInactividad, motivoCierreInactividad } from "../ui/inactividad.js";
import { prepararPendientesAlEntrar } from "../ui/pendientes.js";

// Política de caducidad de contraseñas ({ habilitado, dias }), configurada por
// un administrador en Usuarios. Se lee directo de tenant_config —la fuente de
// verdad— porque este control corre ANTES de cargarConfigsNube.
async function leerPoliticaPassword(tenantId) {
  try {
    const { data } = await supa.from("tenant_config")
      .select("valor").eq("tenant_id", tenantId)
      .eq("clave", "erp:politica_password").maybeSingle();
    return data ? data.valor : null;
  } catch { return null; }
}

export function tienePermiso(pageId) {
  const rol = state.usuarioRol;
  if (!rol || rol === "admin") return true;      // sin rol asignado o admin → acceso total
  if (pageId === "dashboard") return true;         // panel general siempre visible
  const rolPersonalizado = getRolesPersonalizados().find(r => r.id === rol);
  if (rolPersonalizado) return (rolPersonalizado.paginas || []).includes(pageId);
  // Rol de sistema con permisos ajustados por la empresa: manda el ajuste.
  const ajuste = getRolesSistemaOverrides()[rol];
  if (ajuste && Array.isArray(ajuste.paginas)) return ajuste.paginas.includes(pageId);
  const permitidas = ROLE_PAGES[rol];
  return Array.isArray(permitidas) ? permitidas.includes(pageId) : false;
}

// Verificación en dos pasos OBLIGATORIA para todos los usuarios (política de
// seguridad del producto): cualquier cuenta debe registrar un factor TOTP
// (Microsoft Authenticator, Google Authenticator, Authy…) antes de operar.
// Se conserva el parámetro por compatibilidad con quienes llaman a la función.
export function rolExigeMFA(_rol) {
  return true;
}

export async function arrancarDespuesDeLogin(userId, perfilPrecargado, bienvenidaPrecalentada) {
  // A propósito NO se toca #root aquí: reemplazarlo por un fondo hacía que el
  // formulario de login desapareciera de golpe al pulsar "Iniciar sesión". El
  // login permanece visible durante los ~300ms de verificación y la pantalla de
  // bienvenida se funde ENCIMA; cuando se desvanece, debajo ya está el ERP.

  // El perfil puede venir ya pedido desde continuarLoginConMFA, para que su
  // consulta se solape con la validación del dispositivo de confianza en vez de
  // esperar a que termine.
  //
  // getSession() en lugar de getUser(): getUser() hace una petición de red al
  // servidor de Auth, mientras que getSession() lee la sesión local. El correo y
  // app_metadata viajan firmados dentro del JWT recién emitido, así que la copia
  // local es igual de fiable aquí y ahorra un viaje completo.
  const [perfil, resSesion] = await Promise.all([
    perfilPrecargado || obtenerTenantIdDelUsuario(userId),
    supa.auth.getSession().catch(() => null)
  ]);
  if (!perfil || !perfil.tenant_id) {
    bienvenidaPrecalentada?.cerrar();
    await supa.auth.signOut();
    renderPantallaLogin("Tu usuario no tiene una empresa asignada todavía. Contacta al administrador.");
    return;
  }
  cloudState.tenantId = perfil.tenant_id;
  state.usuarioRol = perfil.rol || null;
  state.usuarioNombre = perfil.nombre || null;
  const usuarioActual = resSesion?.data?.session?.user || null;
  state.usuarioEmail = usuarioActual ? usuarioActual.email : null;

  // Verificación en dos pasos obligatoria para todos: si no hay factor TOTP
  // verificado, se fuerza el enrolamiento antes de continuar.
  //
  // Los factores vienen dentro del usuario de la sesión, así que no hace falta
  // pedir listFactors() por red; solo se recurre a esa llamada si el objeto no
  // los trae. (Antes también se cargaban aquí los roles personalizados, para que
  // un rol con MFA exigido no pasara de largo; ya no hace falta porque el MFA es
  // obligatorio para TODOS —rolExigeMFA devuelve siempre true— y los roles se
  // cargan igualmente en cargarConfigsNube antes del primer render.)
  if (rolExigeMFA(state.usuarioRol)) {
    let tieneFactorVerificado = false;
    if (Array.isArray(usuarioActual?.factors)) {
      tieneFactorVerificado = usuarioActual.factors.some(
        f => f.status === "verified" && (!f.factor_type || f.factor_type === "totp")
      );
    } else {
      const { data: factores, error: errFactores } = await supa.auth.mfa.listFactors();
      tieneFactorVerificado = !errFactores && (factores.totp || []).some(f => f.status === "verified");
    }
    if (!tieneFactorVerificado) {
      bienvenidaPrecalentada?.cerrar();
      renderPantallaMFAObligatorio(userId);
      return;
    }
  }

  // Cambio de contraseña forzado por un administrador: si el usuario tiene la
  // marca must_change_password en su app_metadata, no puede entrar hasta fijar
  // una contraseña nueva. (La marca la pone/limpia la edge function admin-usuarios.)
  if (usuarioActual && usuarioActual.app_metadata && usuarioActual.app_metadata.must_change_password) {
    bienvenidaPrecalentada?.cerrar();
    renderPantallaCambioPassword(userId);
    return;
  }

  // Caducidad de contraseña: si la política está activa y el último cambio es
  // más viejo que el plazo, se exige una contraseña nueva antes de entrar.
  // Cuentas sin fecha sellada (anteriores a esta función): se siembra "hoy" en
  // segundo plano — el reloj les arranca desde este inicio de sesión, en vez
  // de expulsar a todo el mundo el día que se active la política.
  const politica = await leerPoliticaPassword(perfil.tenant_id);
  if (politica && politica.habilitado && Number(politica.dias) > 0 && usuarioActual) {
    const sello = usuarioActual.app_metadata && usuarioActual.app_metadata.password_cambiada_at;
    if (!sello) {
      supa.functions.invoke("admin-usuarios", { body: { accion: "sellar_password_fecha" } }).catch(() => {});
    } else if (Date.now() - new Date(sello).getTime() > Number(politica.dias) * 86400000) {
      bienvenidaPrecalentada?.cerrar();
      renderPantallaCambioPassword(userId,
        `Tu contraseña tiene más de ${politica.dias} días. La política de seguridad de la empresa exige renovarla para continuar.`);
      return;
    }
  }

  // Superados los controles de acceso, ya sabemos que el usuario entra: se abre
  // la bienvenida, que sustituye al antiguo texto "Cargando datos de la nube…".
  const rolInfo = ROLES_INFO[state.usuarioRol] || {};
  const datos = {
    nombre: state.usuarioNombre || state.usuarioEmail,
    rolLabel: rolInfo.label || state.usuarioRol || "",
    rolIcon: rolInfo.icon || "",
    empresa: EMPRESA.nombre || ""
  };
  // Si viene precalentada desde el login, solo se revela: los contextos WebGL y
  // los shaders ya están listos, así que el fundido es composición pura. Si no
  // (recarga con sesión abierta), se crea y se revela en el momento.
  const bienvenida = bienvenidaPrecalentada || abrirSplash(datos);
  // revelar() devuelve una promesa que se cumple cuando la capa CUBRE de verdad.
  // No se espera aquí —eso sumaría un segundo al ingreso—: el fundido corre
  // durante las descargas y solo se exige antes de tocar #root, más abajo.
  const bienvenidaCubre = bienvenida.revelar(datos);
  const inicio = Date.now();
  bienvenida.progreso(15, "Verificando sesión…");

  cloudState.syncEnPausa = true;              // no re-subir mientras descargamos
  bienvenida.progreso(40, "Cargando tu empresa…");
  await descargarSnapshotDeNube(cloudState.tenantId); // legado: datos previos a la migración por tablas
  await cargarConfigsNube();                  // configuraciones desde tenant_config (fuente de verdad)
  cloudState.syncEnPausa = false;             // a partir de aquí, cada cambio se sincroniza

  loadData();
  bienvenida.progreso(70, "Descargando documentos…");
  // Estas tres cargas no dependen entre sí: en paralelo, en vez de una tras otra.
  //  · entidades  → sobrescribe con la versión real de las tablas ya migradas
  //  · módulos    → flags administrados desde el panel de propietario
  // El cierre contable ya no se carga: se calcula (último día del mes anterior).
  await Promise.all([
    cargarEntidadesNube(),
    cargarModulosHabilitados(cloudState.tenantId)
  ]);
  suscribirRealtimeTenant(cloudState.tenantId); // refleja en vivo cambios hechos desde otra sesión/dispositivo
  // Si un administrador fuerza o restablece la contraseña de este usuario
  // mientras trabaja, recibe la cuenta regresiva de 3 minutos y la sesión se
  // cierra para que el cambio obligatorio lo detenga al reingresar.
  iniciarAvisosDeSeguridad(userId);
  iniciarVigilanciaInactividad();
  // Reflejo instantáneo: si el propietario prende/apaga un módulo en el panel,
  // el menú se actualiza en caliente (sin recargar). Si la página actual quedó
  // apagada, se vuelve al Panel General.
  suscribirModulosRealtime(cloudState.tenantId, () => {
    if (!moduloHabilitado(state.page)) state.page = "dashboard";
    render();
  });
  if (!tienePermiso(state.page) || !moduloHabilitado(state.page)) state.page = "dashboard"; // rol sin acceso o módulo apagado → panel general
  iniciarTrackerUso(); // medición de tiempo real de uso (Informes → Tiempo de uso)
  anotarIngreso();
  bienvenida.progreso(100, "Preparando el panel…");

  // El ERP se construye DEBAJO de la bienvenida (que es una capa fija aparte),
  // así al desvanecerse ya está todo listo y no se ve ningún salto. Antes de
  // reconstruir #root hay que cerrar el fondo translúcido de la bienvenida: si
  // la carga fue muy rápida, el ERP asomaría a través de él.
  // AQUÍ está la garantía contra el destello claro: render() borra el login de
  // #root y lo sustituye por el ERP, que tiene fondo claro. Si la bienvenida
  // aún no ha terminado su fundido, ese instante queda sin nada oscuro encima
  // —y si el hilo principal se bloquea justo entonces (reconstruir el ERP no es
  // gratis), el cuadro claro se queda congelado en pantalla. Esperar a que la
  // capa cubra no retrasa el ingreso: el fundido ya corrió durante las descargas.
  await bienvenidaCubre;
  bienvenida.solidificar();
  render();
  attachListeners();

  // Si la carga fue más rápida que la animación, se completa el mínimo (que un
  // administrador puede ajustar desde Configuración). La espera nunca se suma:
  // solo rellena lo que falte.
  const minMs = Math.max(0, (getSplashConfig().segundos || 6) * 1000);
  const restante = minMs - (Date.now() - inicio);
  if (restante > 0) await new Promise(r => setTimeout(r, restante));
  bienvenida.cerrar();
  // Con el ERP ya visible: si quedaron documentos en borrador de la sesión
  // anterior, el pop-up de pendientes ofrece retomarlos de a uno.
  prepararPendientesAlEntrar();
}

function anotarIngreso() {
  let pendiente = false;
  try { pendiente = sessionStorage.getItem("aura:ingreso_pendiente") === "1"; sessionStorage.removeItem("aura:ingreso_pendiente"); } catch { /* sin marca */ }
  if (!pendiente) return;
  const instalada = (() => { try { return window.matchMedia("(display-mode: standalone)").matches || navigator.standalone === true; } catch { return false; } })();
  registrarEvento("sesion", state.usuarioEmail || "—", "ingreso", { referencia: state.usuarioEmail, detalle: {
    rol: state.usuarioRol || "", dispositivo: instalada ? "app instalada" : (window.innerWidth <= 768 ? "celular (navegador)" : "navegador")
  } });
}

export async function cerrarSesion() {
  await anotarSalida("salida");
  try { await supa.auth.signOut(); } catch {}
  location.reload();
}

export async function iniciarApp() {
  const { data: { session } } = await supa.auth.getSession();
  if (session && session.user) {
    await continuarLoginConMFA(session.user.id);
  } else {
    // Si el cierre fue por inactividad, el login lo explica: aparecer de vuelta
    // en la puerta sin saber por qué desconcierta más que el cierre mismo.
    renderPantallaLogin(motivoCierreInactividad() || undefined);
  }
}
